import React from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useOrgNavigation, extractBasePath } from '../../utils/navigation';
import {
  Dashboard as DashboardIcon,
  Warning as AlarmIcon,
  Security as AccessControlIcon,
  Receipt as ChargingSessionsIcon,
} from '@mui/icons-material';

interface ChargerNavigationBarProps {
  // Add props as needed
}

const ChargerNavigationBar: React.FC<ChargerNavigationBarProps> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { navigateToOrgPath } = useOrgNavigation();

  const handleItemClick = (path: string) => {
    // Get the current charger ID from the URL
    const basePath = extractBasePath(location.pathname);
    const pathSegments = basePath.split('/');
    const evinyId = pathSegments[3]; // /assets/chargers/evinyId/...
    const fullPath = `/assets/chargers/${evinyId}/${path}`;
    const orgPath = navigateToOrgPath(fullPath);
    navigate(orgPath);
  };

  const isActive = (path: string) => {
    const basePath = extractBasePath(location.pathname);
    return basePath.endsWith(path);
  };

  return (
    <Box sx={{
      backgroundColor: (theme) => theme.palette.secondary.dark + 'BF',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      px: 2,
      py: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'start',
      height: '36px'
    }}>
      {/* Navigation items */}
      <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 0, height: '100%' }}>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            cursor: 'pointer',
            px: 2, // 16px left/right
            py: 1, // 8px top/bottom
            borderRadius: isActive('dashboard') ? 0 : 1,
            backgroundColor: isActive('dashboard') ? (theme) => theme.palette.secondary.dark : 'transparent',
            height: '100%',
            '&:hover': { 
              backgroundColor: isActive('dashboard') ? (theme) => theme.palette.secondary.dark : 'rgba(255, 255, 255, 0.1)',
              borderRadius: 0
            }
          }}
          onClick={() => handleItemClick('dashboard')}
        >
          <DashboardIcon sx={{ 
            color: isActive('dashboard') ? 'white' : 'white',
            fontSize: 20
          }} />
          <Typography 
            variant="body1" 
            sx={{ 
              fontWeight: 500, 
              color: 'white',
            }}
          >
            Dashboard
          </Typography>
        </Box>
        
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            cursor: 'pointer',
            px: 2, // 16px left/right
            py: 1, // 8px top/bottom
            borderRadius: isActive('alarms') ? 0 : 1,
            backgroundColor: isActive('alarms') ? (theme) => theme.palette.secondary.dark : 'transparent',
            height: '100%',
            '&:hover': { 
              backgroundColor: isActive('alarms') ? (theme) => theme.palette.secondary.dark : 'rgba(255, 255, 255, 0.1)',
              borderRadius: 0
            }
          }}
          onClick={() => handleItemClick('alarms')}
        >
          <AlarmIcon sx={{ 
            color: isActive('alarms') ? 'white' : 'white',
            fontSize: 20
          }} />
          <Typography 
            variant="body1" 
            sx={{ 
              fontWeight: 500, 
              color: 'white',
            }}
          >
            Alarm and Events
          </Typography>
        </Box>
        
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            cursor: 'pointer',
            px: 2, // 16px left/right
            py: 1, // 8px top/bottom
            borderRadius: isActive('access-control') ? 0 : 1,
            backgroundColor: isActive('access-control') ? (theme) => theme.palette.secondary.dark : 'transparent',
            height: '100%',
            '&:hover': { 
              backgroundColor: isActive('access-control') ? (theme) => theme.palette.secondary.dark : 'rgba(255, 255, 255, 0.1)',
              borderRadius: 0
            }
          }}
          onClick={() => handleItemClick('access-control')}
        >
          <AccessControlIcon sx={{ 
            color: isActive('access-control') ? 'white' : 'white',
            fontSize: 20
          }} />
          <Typography 
            variant="body1" 
            sx={{ 
              fontWeight: 500, 
              color: 'white',
            }}
          >
            Access Control
          </Typography>
        </Box>
        
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            cursor: 'pointer',
            px: 2, // 16px left/right
            py: 1, // 8px top/bottom
            borderRadius: isActive('charging-sessions') ? 0 : 1,
            backgroundColor: isActive('charging-sessions') ? (theme) => theme.palette.secondary.dark : 'transparent',
            height: '100%',
            '&:hover': { 
              backgroundColor: isActive('charging-sessions') ? (theme) => theme.palette.secondary.dark : 'rgba(255, 255, 255, 0.1)',
              borderRadius: 0
            }
          }}
          onClick={() => handleItemClick('charging-sessions')}
        >
          <ChargingSessionsIcon sx={{ 
            color: isActive('charging-sessions') ? 'white' : 'white',
            fontSize: 20
          }} />
          <Typography 
            variant="body1" 
            sx={{ 
              fontWeight: 500, 
              color: 'white',
            }}
          >
            Charging Sessions
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ChargerNavigationBar;
